import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from 'recharts'
import {
  Sparkle,
  Lightning,
  ArrowUp,
  Tag,
  Clock,
  CheckCircle,
  Robot,
  YoutubeLogo,
  InstagramLogo,
  LinkBreak,
  ArrowClockwise,
  Play,
  FilmSlate,
  Eye,
  ThumbsUp,
  ChartBar,
  CursorClick,
  SquaresFour,
  Heart,
  BookmarkSimple,
  ShareNetwork,
  Users,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import { useLocation, useNavigate } from 'react-router-dom'
import SourcesPanel from '../components/SourcesPanel'
import { useBilling, generateAction } from '../contexts/BillingContext'

const STATUS_LABELS = {
  idea: 'Idea',
  plan: 'Plan',
  record: 'Grabar',
  edit: 'Editar',
  analyze: 'Analizar',
  publish: 'Publicar',
}
const STATUS_COLORS = {
  idea: '#FFD60A',
  plan: '#5E5CE6',
  record: '#D42B1E',
  edit: '#FF9F0A',
  analyze: '#007AFF',
  publish: '#32ADE6',
}
const PLATFORM_COLORS_CHART = {
  YouTube: '#FF0000',
  Instagram: '#E1306C',
  TikTok: '#69C9D0',
  LinkedIn: '#0077B5',
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div
      className="bg-ds-elevated border border-ds-border px-3 py-2 text-white text-xs"
      style={{ borderRadius: 2 }}
    >
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  )
}

function InsightCard({ icon: Icon, title, content, color, type }) {
  const bgMap = {
    info: 'bg-[#007AFF]/5 border-[#007AFF]/20',
    success: 'bg-[#32ADE6]/5 border-[#32ADE6]/20',
    warning: 'bg-[#FF9F0A]/5 border-[#FF9F0A]/20',
    primary: 'bg-[rgba(212,43,30,0.05)] border-[rgba(212,43,30,0.2)]',
  }
  return (
    <div className={`border p-4 ${bgMap[type] || bgMap.info}`} style={{ borderRadius: 2 }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color }} />
        <span className="text-xs font-semibold uppercase tracking-widest" style={{ color }}>
          {title}
        </span>
      </div>
      <div className="text-[#A1A1A6] text-sm leading-relaxed space-y-2">
        {String(content)
          .split(/\n\n+/)
          .map((para, i) => (
            <p key={i}>{para}</p>
          ))}
      </div>
    </div>
  )
}

function formatNumber(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatWatchHours(minutes) {
  return (minutes / 60).toFixed(1) + ' h'
}

function calcEngagementRate(likes, comments, views) {
  if (!views) return '0.00%'
  return (((likes + comments) / views) * 100).toFixed(2) + '%'
}

function SortIcon({ dir }) {
  return <span className="text-[10px] ml-0.5">{dir === 'asc' ? '↑' : '↓'}</span>
}

function KPICard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={14} style={{ color }} weight="duotone" />
        <span className="text-ds-muted text-xs">{label}</span>
      </div>
      <p
        className="text-ds-text text-xl font-bold"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {value}
      </p>
      {subtitle && <p className="text-ds-muted text-xs mt-0.5 truncate">{subtitle}</p>}
    </div>
  )
}

const TRAFFIC_SOURCE_LABELS = {
  YT_SEARCH: 'Búsqueda YouTube',
  SUGGESTED_VIDEO: 'Vídeos sugeridos',
  SUBSCRIBER: 'Suscriptores',
  BROWSE: 'Inicio / Explorar',
  EXT_URL: 'Fuentes externas',
  SHORTS: 'YouTube Shorts',
  NO_LINK_EMBEDDED: 'Embebido',
  END_SCREEN: 'Pantalla final',
  NOTIFICATION: 'Notificaciones',
  PLAYLIST: 'Playlists',
  CAMPAIGN_CARD: 'Tarjetas',
  YT_CHANNEL: 'Página del canal',
  HASHTAG: 'Hashtags',
  LIVE_REDIRECT: 'Redirección directa',
  NO_LINK_OTHER: 'Otras fuentes',
}

function DetailMetric({ label, value, color }) {
  return (
    <div className="bg-ds-elevated p-3" style={{ borderRadius: 2 }}>
      <p className="text-ds-muted text-xs mb-1">{label}</p>
      <p className="text-base font-semibold" style={{ color }}>
        {value}
      </p>
    </div>
  )
}

function RetentionTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div
      className="bg-ds-elevated border border-ds-border px-3 py-2 text-xs"
      style={{ borderRadius: 2 }}
    >
      <p className="text-ds-muted">{(d.ratio * 100).toFixed(0)}% del vídeo</p>
      <p className="text-ds-text font-semibold">{(d.watch_ratio * 100).toFixed(1)}% retención</p>
      {d.relative_retention !== undefined && (
        <p style={{ color: d.relative_retention >= 1 ? '#30D158' : '#FF9F0A' }}>
          {d.relative_retention >= 1 ? '▲' : '▼'} vs. promedio canal
        </p>
      )}
    </div>
  )
}

function AnalyticsDetail({ detail }) {
  if (!detail || !detail.basicStatus || detail.basicStatus === 'loading') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="h-16 bg-[#1E1E1E] rounded-sharp animate-pulse" />
          ))}
        </div>
        <div className="h-32 bg-[#1E1E1E] rounded-sharp animate-pulse" />
        <div className="h-24 bg-[#1E1E1E] rounded-sharp animate-pulse" />
      </div>
    )
  }

  if (detail.basicStatus === 'error') {
    return (
      <p className="text-[#6E6E73] text-xs text-center py-2">No se pudieron cargar las métricas.</p>
    )
  }

  const { basic, retention, traffic, detailStatus } = detail

  // Retention: mark biggest drop point
  const biggestDrop =
    retention.length > 1
      ? retention.slice(1).reduce(
          (worst, pt, i) => {
            const drop = retention[i].watch_ratio - pt.watch_ratio
            return drop > worst.drop ? { drop, ratio: pt.ratio } : worst
          },
          { drop: 0, ratio: 0 }
        )
      : null

  // Traffic: total views for % calculation
  const totalTrafficViews = traffic.reduce((s, t) => s + t.views, 0)

  return (
    <div className="space-y-5">
      {/* Basic metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <DetailMetric
          label="Duración media"
          value={formatDuration(basic.avg_view_duration_seconds)}
          color="#BF5AF2"
        />
        <DetailMetric
          label="% vídeo visto"
          value={`${basic.avg_view_percentage?.toFixed(1) ?? '—'}%`}
          color="#BF5AF2"
        />
        <DetailMetric
          label="Tiempo visionado"
          value={formatWatchHours(basic.watch_time_minutes)}
          color="#FF9F0A"
        />
        <DetailMetric label="Me gusta" value={formatNumber(basic.likes)} color="#30D158" />
        <DetailMetric label="Comentarios" value={formatNumber(basic.comments)} color="#A1A1A6" />
        <DetailMetric label="Compartidos" value={formatNumber(basic.shares)} color="#0A84FF" />
        <DetailMetric label="No me gusta" value={formatNumber(basic.dislikes)} color="#6E6E73" />
        <DetailMetric
          label="Engagement"
          value={calcEngagementRate(basic.likes, basic.comments, basic.views)}
          color="#FF375F"
        />
      </div>

      {/* Detail sections: retention + traffic */}
      {detailStatus === 'loading' && (
        <div className="space-y-3">
          <div className="h-32 bg-[#1E1E1E] rounded-sharp animate-pulse" />
          <div className="h-24 bg-[#1E1E1E] rounded-sharp animate-pulse" />
        </div>
      )}

      {detailStatus === 'loaded' && (
        <>
          {/* Retention curve */}
          {retention.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-xs font-semibold">Curva de retención</p>
                {biggestDrop && biggestDrop.drop > 0.05 && (
                  <p className="text-[#FF9F0A] text-xs">
                    Mayor caída a los {(biggestDrop.ratio * 100).toFixed(0)}%
                  </p>
                )}
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <LineChart data={retention} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis
                    dataKey="ratio"
                    tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                    tick={{ fill: '#6E6E73', fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                    tick={{ fill: '#6E6E73', fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                    domain={[0, 'auto']}
                  />
                  <Tooltip content={<RetentionTooltip />} />
                  <ReferenceLine
                    x={retention.find(p => p.ratio >= 0.05)?.ratio ?? 0.05}
                    stroke="rgba(255,159,10,0.3)"
                    strokeDasharray="3 3"
                    label={{ value: 'Hook', fill: '#FF9F0A', fontSize: 9, position: 'top' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="watch_ratio"
                    stroke="#BF5AF2"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3, fill: '#BF5AF2' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Traffic sources */}
          {traffic.length > 0 && (
            <div>
              <p className="text-white text-xs font-semibold mb-2">Fuentes de tráfico</p>
              <div className="space-y-1.5">
                {traffic.slice(0, 6).map(t => {
                  const label = TRAFFIC_SOURCE_LABELS[t.source] || t.source
                  const pct = totalTrafficViews > 0 ? (t.views / totalTrafficViews) * 100 : 0
                  return (
                    <div key={t.source} className="flex items-center gap-2">
                      <span className="text-ds-muted text-xs w-36 shrink-0 truncate">{label}</span>
                      <div
                        className="flex-1 h-1.5 bg-ds-elevated overflow-hidden"
                        style={{ borderRadius: 1 }}
                      >
                        <div
                          className="h-full bg-ds-red"
                          style={{ width: `${pct}%`, borderRadius: 1 }}
                        />
                      </div>
                      <span className="text-ds-muted text-xs w-16 text-right shrink-0">
                        {formatNumber(t.views)} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}

      {basic.fetched_at && (
        <p className="text-[#3E3E43] text-xs text-right">
          Actualizado:{' '}
          {new Date(basic.fetched_at).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}
    </div>
  )
}

function VideoRow({ video, isExpanded, detail, onToggle }) {
  return (
    <div
      className="bg-ds-elevated border border-ds-border overflow-hidden hover:border-ds-border-mid transition-colors"
      style={{ borderRadius: 2 }}
    >
      <div className="p-3 flex items-center gap-3">
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-20 h-12 rounded object-cover shrink-0"
          />
        ) : (
          <div
            className="w-20 h-12 bg-ds-surface flex items-center justify-center shrink-0"
            style={{ borderRadius: 2 }}
          >
            <Play size={16} className="text-ds-muted" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-ds-text text-sm font-medium truncate">{video.title}</p>
          <p className="text-ds-muted text-xs mt-0.5">
            {new Date(video.published_at).toLocaleDateString('es-ES')}
          </p>
        </div>
        <div className="shrink-0 text-right w-24 hidden sm:block">
          <p className="text-ds-text text-sm font-semibold">{formatNumber(video.view_count)}</p>
          <p className="text-ds-muted text-xs">{formatNumber(video.like_count)} likes</p>
        </div>
        <button
          onClick={onToggle}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
            isExpanded
              ? 'bg-[rgba(212,43,30,0.08)] text-ds-red border border-[rgba(212,43,30,0.2)]'
              : 'bg-ds-surface text-ds-muted border border-ds-border hover:text-ds-text'
          }`}
          style={{ borderRadius: 2 }}
        >
          <ChartBar size={13} />
          {isExpanded ? 'Cerrar' : 'Ver métricas'}
        </button>
      </div>
      {isExpanded && (
        <div className="border-t border-ds-border p-4 bg-ds-bg">
          <AnalyticsDetail detail={detail} />
        </div>
      )}
    </div>
  )
}

function YouTubeTab() {
  const location = useLocation()
  const [status, setStatus] = useState(null)
  const [videos, setVideos] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [connectingOAuth, setConnectingOAuth] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [analyticsCache, setAnalyticsCache] = useState({})
  const [expandedVideoId, setExpandedVideoId] = useState(null)
  const [sortKey, setSortKey] = useState('view_count')
  const [sortDir, setSortDir] = useState('desc')
  const [platformFilter, setPlatformFilter] = useState('all')
  const [projectPlatformMap, setProjectPlatformMap] = useState({}) // { youtube_video_id: platform }

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/youtube/status')
      setStatus(data)
    } catch {
      setStatus({ connected: false })
    }
  }, [])

  const preloadBasics = useCallback(async videoList => {
    if (!videoList.length) return
    // Initialise all entries as loading
    setAnalyticsCache(
      videoList.reduce((acc, v) => {
        acc[v.video_id] = {
          basicStatus: 'loading',
          detailStatus: null,
          basic: null,
          retention: [],
          traffic: [],
        }
        return acc
      }, {})
    )
    // Fetch all in parallel — results are cached in MongoDB (1 h TTL)
    await Promise.allSettled(
      videoList.map(async video => {
        try {
          const { data } = await api.get(`/youtube/videos/${video.video_id}/analytics`)
          setAnalyticsCache(prev => ({
            ...prev,
            [video.video_id]: { ...prev[video.video_id], basicStatus: 'loaded', basic: data },
          }))
        } catch {
          setAnalyticsCache(prev => ({
            ...prev,
            [video.video_id]: { ...prev[video.video_id], basicStatus: 'error' },
          }))
        }
      })
    )
  }, [])

  const fetchVideos = useCallback(async () => {
    setLoadingVideos(true)
    try {
      const { data } = await api.get('/youtube/videos')
      const videoList = data.videos || []
      setVideos(videoList)
      preloadBasics(videoList)
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar vídeos')
    } finally {
      setLoadingVideos(false)
    }
  }, [preloadBasics])

  useEffect(() => {
    fetchStatus()
    // Cargar proyectos para saber qué plataforma tiene cada youtube_video_id
    api
      .get('/projects')
      .then(r => {
        const map = {}
        for (const p of r.data) {
          if (p.youtube_video_id) map[p.youtube_video_id] = p.platform || 'YouTube'
        }
        setProjectPlatformMap(map)
      })
      .catch(() => {})
  }, [fetchStatus])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('connected') === 'true') {
      toast.success('¡Canal de YouTube conectado!')
      window.history.replaceState({}, '', '/analysis')
      fetchStatus()
    }
  }, [location.search, fetchStatus])

  useEffect(() => {
    if (status?.connected) fetchVideos()
  }, [status?.connected, fetchVideos])

  const handleConnect = async () => {
    setConnectingOAuth(true)
    try {
      const { data } = await api.get('/youtube/oauth/start')
      window.location.href = data.auth_url
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar OAuth')
      setConnectingOAuth(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar canal de YouTube?')) return
    setDisconnecting(true)
    try {
      await api.delete('/youtube/disconnect')
      setStatus({ connected: false })
      setVideos([])
      setAnalyticsCache({})
      toast.success('Canal desconectado')
    } catch {
      toast.error('Error al desconectar')
    } finally {
      setDisconnecting(false)
    }
  }

  const handleLoadAnalytics = async videoId => {
    if (expandedVideoId === videoId) {
      setExpandedVideoId(null)
      return
    }
    setExpandedVideoId(videoId)
    // Detail (retention + traffic) already loaded — nothing to do
    if (analyticsCache[videoId]?.detailStatus === 'loaded') return
    setAnalyticsCache(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], detailStatus: 'loading' },
    }))
    try {
      const [retentionRes, trafficRes] = await Promise.allSettled([
        api.get(`/youtube/videos/${videoId}/retention`),
        api.get(`/youtube/videos/${videoId}/traffic-sources`),
      ])
      setAnalyticsCache(prev => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          detailStatus: 'loaded',
          retention:
            retentionRes.status === 'fulfilled' ? (retentionRes.value.data.curve ?? []) : [],
          traffic: trafficRes.status === 'fulfilled' ? (trafficRes.value.data.sources ?? []) : [],
        },
      }))
    } catch {
      setAnalyticsCache(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], detailStatus: 'error' },
      }))
      toast.error('Error al cargar métricas avanzadas')
    }
  }

  const handleSort = key => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  if (status === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-ds-red border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!status.connected) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div
          className="w-16 h-16 bg-[rgba(255,0,0,0.08)] border border-[rgba(255,0,0,0.2)] flex items-center justify-center mb-4"
          style={{ borderRadius: 2 }}
        >
          <YoutubeLogo size={32} className="text-[#FF0000]" weight="fill" />
        </div>
        <h2
          className="text-ds-text text-xl font-bold mb-2"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Conecta tu canal de YouTube
        </h2>
        <p className="text-ds-muted text-sm max-w-sm mb-6">
          Vincula tu canal para ver métricas reales de tus vídeos directamente en tus proyectos.
        </p>
        <button
          onClick={handleConnect}
          disabled={connectingOAuth}
          className="flex items-center gap-2 bg-[#FF0000] hover:bg-[#CC0000] text-white px-6 py-3 font-semibold text-sm transition-colors disabled:opacity-50"
          style={{ borderRadius: 2 }}
        >
          {connectingOAuth ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <YoutubeLogo size={18} weight="fill" />
          )}
          {connectingOAuth ? 'Redirigiendo...' : 'Conectar con YouTube'}
        </button>
      </div>
    )
  }

  // Filtrar vídeos por plataforma según los proyectos vinculados
  const YOUTUBE_PLATS = new Set(['YouTube', 'YouTube largo', 'youtube'])
  const SHORTS_PLATS = new Set(['YouTube Shorts', 'Shorts', 'shorts'])
  const displayVideos =
    platformFilter === 'Shorts'
      ? videos.filter(v => SHORTS_PLATS.has(projectPlatformMap[v.video_id]))
      : platformFilter === 'YouTube'
        ? videos.filter(v => {
            const plat = projectPlatformMap[v.video_id]
            return !plat || YOUTUBE_PLATS.has(plat)
          })
        : videos // 'all'

  const totalViews = displayVideos.reduce((s, v) => s + (v.view_count || 0), 0)
  const totalLikes = displayVideos.reduce((s, v) => s + (v.like_count || 0), 0)

  const displayVideoIds = new Set(displayVideos.map(v => v.video_id))
  const loadedBasics = Object.entries(analyticsCache)
    .filter(
      ([videoId, d]) => d?.basicStatus === 'loaded' && d.basic && displayVideoIds.has(videoId)
    )
    .map(([videoId, d]) => ({ ...d.basic, video_id: videoId }))
  const bestDurationEntry =
    loadedBasics.length > 0
      ? loadedBasics.reduce(
          (best, a) => (a.avg_view_duration_seconds > best.avg_view_duration_seconds ? a : best),
          loadedBasics[0]
        )
      : null
  const bestDurationVideo = bestDurationEntry
    ? displayVideos.find(v => v.video_id === bestDurationEntry.video_id)
    : null
  const avgViewPercentage =
    loadedBasics.length > 0
      ? loadedBasics.reduce((sum, a) => sum + (a.avg_view_percentage || 0), 0) / loadedBasics.length
      : null
  const basicLoadingCount = Object.values(analyticsCache).filter(
    d => d?.basicStatus === 'loading'
  ).length

  const top5 = [...displayVideos]
    .sort((a, b) => b.view_count - a.view_count)
    .slice(0, 5)
    .map(v => ({
      name: v.title.length > 22 ? v.title.slice(0, 22) + '…' : v.title,
      views: v.view_count,
    }))

  const sortedVideos = [...displayVideos].sort((a, b) => {
    if (sortKey === 'published_at') {
      const diff = new Date(a.published_at).getTime() - new Date(b.published_at).getTime()
      return sortDir === 'asc' ? diff : -diff
    }
    if (sortKey === 'title') {
      return sortDir === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
    }
    const diff = (a[sortKey] || 0) - (b[sortKey] || 0)
    return sortDir === 'asc' ? diff : -diff
  })

  const SortBtn = ({ k, children }) => (
    <button
      onClick={() => handleSort(k)}
      className={`flex items-center gap-0.5 text-xs transition-colors ${sortKey === k ? 'text-white' : 'text-[#6E6E73] hover:text-[#A1A1A6]'}`}
    >
      {children}
      {sortKey === k && <SortIcon dir={sortDir} />}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Channel header */}
      <div className="card p-4 flex items-center gap-4">
        {status.channel_thumbnail ? (
          <img
            src={status.channel_thumbnail}
            alt="canal"
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[rgba(255,0,0,0.08)] flex items-center justify-center shrink-0">
            <YoutubeLogo size={22} className="text-[#FF0000]" weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p
            className="text-ds-text font-semibold truncate"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {status.channel_name || 'Mi canal'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchVideos}
            disabled={loadingVideos}
            className="p-2 text-ds-muted hover:text-ds-text transition-colors"
            title="Actualizar"
          >
            <ArrowClockwise size={16} className={loadingVideos ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-ds-border text-ds-muted hover:text-ds-red hover:border-[rgba(212,43,30,0.3)] text-xs transition-colors"
            style={{ borderRadius: 2 }}
          >
            <LinkBreak size={13} />
            Desconectar
          </button>
        </div>
      </div>

      {/* Platform filter tabs */}
      {videos.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {[
            { key: 'all', label: 'Todos' },
            { key: 'YouTube', label: 'YouTube Largo' },
            { key: 'Shorts', label: 'Shorts' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setPlatformFilter(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                platformFilter === tab.key
                  ? 'bg-[rgba(255,0,0,0.1)] text-[#FF0000] border border-[rgba(255,0,0,0.2)]'
                  : 'text-ds-muted border border-ds-border hover:text-ds-text hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
            >
              {tab.label}
              {tab.key !== 'all' && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  {tab.key === 'YouTube'
                    ? videos.filter(v => {
                        const p = projectPlatformMap[v.video_id]
                        return !p || YOUTUBE_PLATS.has(p)
                      }).length
                    : videos.filter(v => SHORTS_PLATS.has(projectPlatformMap[v.video_id])).length}
                </span>
              )}
            </button>
          ))}
          {['LinkedIn', 'Instagram', 'TikTok'].map(p => (
            <span
              key={p}
              className="px-3 py-1.5 text-xs text-ds-faint border border-ds-border cursor-not-allowed"
              style={{ borderRadius: 2 }}
              title="Integración disponible próximamente"
            >
              {p}
              <span className="ml-1.5 text-[9px] opacity-60">Próximamente</span>
            </span>
          ))}
        </div>
      )}

      {loadingVideos ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="h-20 bg-ds-elevated animate-pulse border border-ds-border"
              style={{ borderRadius: 2 }}
            />
          ))}
        </div>
      ) : displayVideos.length === 0 ? (
        <div className="card p-8 text-center">
          <FilmSlate size={32} className="text-ds-faint mx-auto mb-3" weight="duotone" />
          <p className="text-ds-muted text-sm">
            {videos.length === 0
              ? 'No se encontraron vídeos en este canal'
              : `No hay vídeos de ${platformFilter === 'Shorts' ? 'YouTube Shorts' : 'YouTube Largo'} vinculados a proyectos`}
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPICard
              label="Visualizaciones totales"
              value={formatNumber(totalViews)}
              icon={Eye}
              color="#D42B1E"
            />
            <KPICard
              label="Me gusta totales"
              value={formatNumber(totalLikes)}
              icon={ThumbsUp}
              color="#FF9F0A"
            />
            <KPICard
              label="Vídeos publicados"
              value={String(displayVideos.length)}
              icon={FilmSlate}
              color="#30D158"
            />
            <KPICard
              label="Mayor duración media"
              value={
                bestDurationEntry
                  ? formatDuration(bestDurationEntry.avg_view_duration_seconds)
                  : basicLoadingCount > 0
                    ? '…'
                    : '—'
              }
              subtitle={bestDurationVideo ? bestDurationVideo.title : undefined}
              icon={CursorClick}
              color="#0A84FF"
            />
            <KPICard
              label="% Vídeo visto (promedio)"
              value={
                avgViewPercentage != null
                  ? `${avgViewPercentage.toFixed(1)}%`
                  : basicLoadingCount > 0
                    ? '…'
                    : '—'
              }
              subtitle={loadedBasics.length > 0 ? `${loadedBasics.length} vídeos` : undefined}
              icon={ChartBar}
              color="#BF5AF2"
            />
          </div>

          {/* Top 5 bar chart */}
          {top5.length > 0 && (
            <div className="card p-5">
              <h3
                className="text-ds-text text-sm font-semibold mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Top {top5.length} vídeos por visualizaciones
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={top5} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#6E6E73', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#6E6E73', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                  />
                  <Bar dataKey="views" fill="#FF0000" radius={[4, 4, 0, 0]} maxBarSize={60} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Sortable video table */}
          <div>
            <div className="flex items-center gap-3 px-3 py-1 mb-1">
              <div className="w-20 shrink-0 hidden sm:block" />
              <div className="flex-1">
                <SortBtn k="title">Título</SortBtn>
              </div>
              <div className="w-24 text-right hidden sm:flex justify-end">
                <SortBtn k="view_count">Vistas</SortBtn>
              </div>
              <div className="w-28 text-right hidden md:flex justify-end">
                <SortBtn k="published_at">Fecha</SortBtn>
              </div>
              <div className="w-28 shrink-0" />
            </div>
            <div className="space-y-2">
              {sortedVideos.map(video => (
                <VideoRow
                  key={video.video_id}
                  video={video}
                  isExpanded={expandedVideoId === video.video_id}
                  detail={analyticsCache[video.video_id]}
                  onToggle={() => handleLoadAnalytics(video.video_id)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Instagram Tab ─────────────────────────────────────────────────────────────

const IG_COLOR = '#E1306C'

function InstagramMediaCard({ item }) {
  const isReel = item.media_type === 'REELS' || item.media_type === 'VIDEO'
  const caption = item.caption
    ? item.caption.length > 80
      ? item.caption.slice(0, 80) + '…'
      : item.caption
    : '—'

  const date = item.timestamp
    ? new Date(item.timestamp).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
      })
    : ''

  return (
    <a
      href={item.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="card p-4 block hover:border-[rgba(225,48,108,0.3)] transition-colors"
    >
      <div className="flex items-start gap-3">
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt=""
            className="w-14 h-14 object-cover shrink-0"
            style={{ borderRadius: 2 }}
          />
        ) : (
          <div
            className="w-14 h-14 bg-[rgba(225,48,108,0.08)] flex items-center justify-center shrink-0"
            style={{ borderRadius: 2 }}
          >
            <InstagramLogo size={22} style={{ color: IG_COLOR }} weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isReel && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 bg-[rgba(225,48,108,0.1)] text-[#E1306C] border border-[rgba(225,48,108,0.2)]"
                style={{ borderRadius: 2 }}
              >
                REEL
              </span>
            )}
            <span className="text-ds-faint text-xs">{date}</span>
          </div>
          <p className="text-ds-muted text-xs leading-relaxed">{caption}</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-ds-border">
        <div className="text-center">
          <p
            className="text-ds-text text-sm font-bold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {formatNumber(item.likes)}
          </p>
          <p className="text-ds-faint text-[10px] flex items-center justify-center gap-0.5 mt-0.5">
            <Heart size={10} style={{ color: IG_COLOR }} weight="fill" />
            Likes
          </p>
        </div>
        <div className="text-center">
          <p
            className="text-ds-text text-sm font-bold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {formatNumber(item.reach)}
          </p>
          <p className="text-ds-faint text-[10px] flex items-center justify-center gap-0.5 mt-0.5">
            <Eye size={10} className="text-ds-faint" />
            Alcance
          </p>
        </div>
        <div className="text-center">
          <p
            className="text-ds-text text-sm font-bold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {formatNumber(item.saves)}
          </p>
          <p className="text-ds-faint text-[10px] flex items-center justify-center gap-0.5 mt-0.5">
            <BookmarkSimple size={10} className="text-ds-faint" />
            Guardados
          </p>
        </div>
        {isReel && item.plays != null ? (
          <div className="text-center">
            <p
              className="text-ds-text text-sm font-bold"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {formatNumber(item.plays)}
            </p>
            <p className="text-ds-faint text-[10px] flex items-center justify-center gap-0.5 mt-0.5">
              <Play size={10} className="text-ds-faint" weight="fill" />
              Plays
            </p>
          </div>
        ) : (
          <div className="text-center">
            <p
              className="text-ds-text text-sm font-bold"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {formatNumber(item.shares)}
            </p>
            <p className="text-ds-faint text-[10px] flex items-center justify-center gap-0.5 mt-0.5">
              <ShareNetwork size={10} className="text-ds-faint" />
              Compartidos
            </p>
          </div>
        )}
      </div>
    </a>
  )
}

function InstagramTab() {
  const location = useLocation()
  const [status, setStatus] = useState(null)
  const [media, setMedia] = useState([])
  const [insights, setInsights] = useState(null)
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [connectingOAuth, setConnectingOAuth] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [fetchedAt, setFetchedAt] = useState(null)

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/instagram/status')
      setStatus(data)
    } catch {
      setStatus({ connected: false })
    }
  }, [])

  const fetchMedia = useCallback(async (forceRefresh = false) => {
    setLoadingMedia(true)
    try {
      const { data } = await api.get('/instagram/media', {
        params: forceRefresh ? { refresh: true } : {},
      })
      setMedia(data.media || [])
      setFetchedAt(data.fetched_at || null)
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar posts de Instagram')
    } finally {
      setLoadingMedia(false)
    }
  }, [])

  const fetchInsights = useCallback(async () => {
    try {
      const { data } = await api.get('/instagram/insights')
      setInsights(data)
    } catch {
      // Insights may fail if App Review not approved — fail silently
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('connected') === 'true' && params.get('tab') === 'instagram') {
      toast.success('¡Instagram conectado!')
      window.history.replaceState({}, '', '/analysis?tab=instagram')
      fetchStatus()
    }
  }, [location.search, fetchStatus])

  useEffect(() => {
    if (status?.connected) {
      fetchMedia()
      fetchInsights()
    }
  }, [status?.connected, fetchMedia, fetchInsights])

  const handleConnect = async () => {
    setConnectingOAuth(true)
    try {
      const { data } = await api.get('/instagram/oauth/start')
      window.location.href = data.auth_url
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar OAuth')
      setConnectingOAuth(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar cuenta de Instagram?')) return
    setDisconnecting(true)
    try {
      await api.delete('/instagram/disconnect')
      setStatus({ connected: false })
      setMedia([])
      setInsights(null)
      toast.success('Cuenta desconectada')
    } catch {
      toast.error('Error al desconectar')
    } finally {
      setDisconnecting(false)
    }
  }

  if (status === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: `${IG_COLOR} transparent transparent transparent` }}
        />
      </div>
    )
  }

  if (!status.connected) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div
          className="w-16 h-16 bg-[rgba(225,48,108,0.08)] border border-[rgba(225,48,108,0.2)] flex items-center justify-center mb-4"
          style={{ borderRadius: 2 }}
        >
          <InstagramLogo size={32} style={{ color: IG_COLOR }} weight="fill" />
        </div>
        <h2
          className="text-ds-text text-xl font-bold mb-2"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Conecta tu cuenta de Instagram
        </h2>
        <p className="text-ds-muted text-sm max-w-sm mb-2">
          Vincula tu cuenta Business o Creator para ver métricas de tus posts y Reels.
        </p>
        <p className="text-ds-faint text-xs max-w-xs mb-6">
          Requiere cuenta Professional (Business o Creator). Las cuentas personales no son
          compatibles.
        </p>
        <button
          onClick={handleConnect}
          disabled={connectingOAuth}
          className="flex items-center gap-2 text-white px-6 py-3 font-semibold text-sm transition-opacity disabled:opacity-50"
          style={{
            background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
            borderRadius: 2,
          }}
        >
          {connectingOAuth ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <InstagramLogo size={18} weight="fill" />
          )}
          {connectingOAuth ? 'Redirigiendo...' : 'Conectar con Instagram'}
        </button>
      </div>
    )
  }

  const totalLikes = media.reduce((s, m) => s + (m.likes || 0), 0)
  const totalReach = media.reduce((s, m) => s + (m.reach || 0), 0)
  const totalSaves = media.reduce((s, m) => s + (m.saves || 0), 0)
  const reelsCount = media.filter(m => m.media_type === 'REELS' || m.media_type === 'VIDEO').length

  const lastUpdated = fetchedAt
    ? (() => {
        const mins = Math.round((Date.now() - new Date(fetchedAt).getTime()) / 60000)
        return mins < 1 ? 'ahora mismo' : `hace ${mins} min`
      })()
    : null

  return (
    <div className="space-y-6">
      {/* Account header */}
      <div className="card p-4 flex items-center gap-4">
        {status.profile_picture_url ? (
          <img
            src={status.profile_picture_url}
            alt="perfil"
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[rgba(225,48,108,0.08)] flex items-center justify-center shrink-0">
            <InstagramLogo size={22} style={{ color: IG_COLOR }} weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p
            className="text-ds-text font-semibold truncate"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            @{status.username || 'mi cuenta'}
          </p>
          {status.account_type && (
            <p className="text-ds-faint text-xs capitalize">{status.account_type.toLowerCase()}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-ds-faint text-xs hidden sm:block">Actualizado {lastUpdated}</span>
          )}
          <button
            onClick={() => fetchMedia(true)}
            disabled={loadingMedia}
            className="p-2 text-ds-muted hover:text-ds-text transition-colors"
            title="Actualizar"
          >
            <ArrowClockwise size={16} className={loadingMedia ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-ds-border text-ds-muted hover:text-ds-red hover:border-[rgba(212,43,30,0.3)] text-xs transition-colors"
            style={{ borderRadius: 2 }}
          >
            <LinkBreak size={13} />
            Desconectar
          </button>
        </div>
      </div>

      {/* KPI cards */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KPICard
            label="Total publicaciones"
            value={String(media.length)}
            icon={InstagramLogo}
            color={IG_COLOR}
          />
          <KPICard
            label="Total likes"
            value={formatNumber(totalLikes)}
            icon={Heart}
            color={IG_COLOR}
          />
          <KPICard
            label="Alcance total"
            value={formatNumber(totalReach)}
            icon={Eye}
            color="#FF9F0A"
          />
          <KPICard
            label="Guardados totales"
            value={formatNumber(totalSaves)}
            icon={BookmarkSimple}
            color="#30D158"
          />
          {insights?.total_followers != null && (
            <KPICard
              label="Seguidores"
              value={formatNumber(insights.total_followers)}
              icon={Users}
              color="#0A84FF"
            />
          )}
          {reelsCount > 0 && (
            <KPICard label="Reels" value={String(reelsCount)} icon={Play} color="#BF5AF2" />
          )}
        </div>
      )}

      {/* Media grid */}
      {loadingMedia ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-36 bg-ds-elevated animate-pulse border border-ds-border"
              style={{ borderRadius: 2 }}
            />
          ))}
        </div>
      ) : media.length === 0 ? (
        <div className="card p-8 text-center">
          <InstagramLogo size={32} className="text-ds-faint mx-auto mb-3" weight="duotone" />
          <p className="text-ds-muted text-sm">No se encontraron publicaciones en esta cuenta</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {media.map(item => (
            <InstagramMediaCard key={item.media_id} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Dashboard Tab ─────────────────────────────────────────────────────────────

function DashboardTab({ projects, loadingProjects }) {
  if (loadingProjects) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="h-56 bg-ds-elevated animate-pulse border border-ds-border"
            style={{ borderRadius: 2 }}
          />
        ))}
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="card p-8 text-center">
        <SquaresFour size={36} className="text-ds-faint mx-auto mb-3" weight="duotone" />
        <p className="text-ds-muted text-sm">Crea proyectos para ver el dashboard de métricas</p>
      </div>
    )
  }

  const platformData = Object.entries(
    projects.reduce((acc, p) => {
      acc[p.platform] = (acc[p.platform] || 0) + 1
      return acc
    }, {})
  ).map(([name, count]) => ({ name, count }))

  const statusData = Object.entries(STATUS_LABELS)
    .map(([key, label]) => ({
      name: label,
      count: projects.filter(p => p.status === key).length,
      color: STATUS_COLORS[key],
    }))
    .filter(d => d.count > 0)

  const tagFreq = projects
    .flatMap(p => p.tags || [])
    .reduce((acc, t) => {
      acc[t] = (acc[t] || 0) + 1
      return acc
    }, {})
  const tagData = Object.entries(tagFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([tag, count]) => ({ name: `#${tag}`, count }))

  const durationData = [
    {
      range: '1-2 min',
      count: projects.filter(p => p.duration_min >= 1 && p.duration_min <= 2).length,
    },
    {
      range: '3-5 min',
      count: projects.filter(p => p.duration_min >= 3 && p.duration_min <= 5).length,
    },
    {
      range: '6-10 min',
      count: projects.filter(p => p.duration_min >= 6 && p.duration_min <= 10).length,
    },
    { range: '10+ min', count: projects.filter(p => p.duration_min > 10).length },
  ].filter(d => d.count > 0)

  const published = projects.filter(p => p.status === 'publish').length
  const linkedInLinked = projects.filter(p => p.linkedin_post_urn).length
  const ytLinked = projects.filter(p => p.youtube_video_id).length
  const platforms = [...new Set(projects.map(p => p.platform).filter(Boolean))]

  return (
    <div className="space-y-6">
      {/* Cross-platform KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard
          label="Proyectos totales"
          value={String(projects.length)}
          icon={FilmSlate}
          color="#D42B1E"
        />
        <KPICard label="Publicados" value={String(published)} icon={CheckCircle} color="#30D158" />
        <KPICard
          label="Plataformas activas"
          value={String(platforms.length)}
          icon={SquaresFour}
          color="#007AFF"
        />
        <KPICard
          label="Posts vinculados"
          value={`YT ${ytLinked} · LI ${linkedInLinked}`}
          icon={ChartBar}
          color="#FF9F0A"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platformData.length > 0 && (
          <div className="card p-5" data-testid="platform-chart">
            <h3
              className="text-ds-text text-sm font-semibold mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Proyectos por Plataforma
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={platformData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
                  {platformData.map((entry, i) => (
                    <Cell key={i} fill={PLATFORM_COLORS_CHART[entry.name] || '#D42B1E'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {statusData.length > 0 && (
          <div className="card p-5" data-testid="status-chart">
            <h3
              className="text-ds-text text-sm font-semibold mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Pipeline de Producción
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
                  {statusData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {tagData.length > 0 && (
          <div className="card p-5" data-testid="tags-chart">
            <h3
              className="text-ds-text text-sm font-semibold mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Tags más Usados
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={tagData}
                layout="vertical"
                margin={{ top: 0, right: 20, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fill: '#A1A1A6', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" fill="#007AFF" radius={[0, 4, 4, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {durationData.length > 0 && (
          <div className="card p-5" data-testid="duration-chart">
            <h3
              className="text-ds-text text-sm font-semibold mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Distribución por Duración
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={durationData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="range"
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#6E6E73', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" fill="#FF9F0A" radius={[4, 4, 0, 0]} maxBarSize={60} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main AnalysisPage ─────────────────────────────────────────────────────────

export default function AnalysisPage() {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [projects, setProjects] = useState([])
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingProjects, setLoadingProjects] = useState(true)
  const navigate = useNavigate()
  // El coste del análisis va en su botón, igual que en el editor.
  const { credits, creditWeights, hasByok } = useBilling()
  const analyzeAction = generateAction(creditWeights?.analyze_patterns, {
    hasByok,
    total: credits?.total,
    verb: 'Analizar patrones',
  })

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      const res = await api.get('/projects')
      setProjects(res.data)
    } catch {
    } finally {
      setLoadingProjects(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
    api
      .get('/ai/analyze')
      .then(res => {
        if (res.data) setAnalysis(res.data)
      })
      .catch(() => {})
  }, [fetchProjects])

  // Switch to the correct platform tab when coming back from OAuth
  const location = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('connected') === 'true') {
      const tab = params.get('tab')
      setActiveTab(tab && ['youtube', 'instagram'].includes(tab) ? tab : 'youtube')
    }
  }, [location.search])

  const runAnalysis = async () => {
    setLoading(true)
    try {
      const res = await api.post('/ai/analyze')
      setAnalysis(res.data)
      toast.success('Análisis completado')
    } catch (err) {
      toast.error('Error al analizar')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: SquaresFour },
    { id: 'ai', label: 'Análisis IA', icon: Robot },
    { id: 'youtube', label: 'YouTube', icon: YoutubeLogo },
    { id: 'instagram', label: 'Instagram', icon: InstagramLogo },
  ]

  return (
    <div className="p-6 lg:p-8 min-h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="type-h2 text-ds-text text-2xl">Análisis</h1>
          <p className="text-ds-muted text-sm mt-0.5">Métricas e insights de tu contenido</p>
        </div>
        {activeTab === 'ai' && (
          <button
            onClick={() => (analyzeAction.blocked ? navigate('/pricing') : runAnalysis())}
            disabled={loading || projects.length === 0}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
              analyzeAction.blocked
                ? 'bg-ds-elevated border border-ds-border-mid text-[#555555] hover:text-ds-text'
                : 'bg-[#007AFF] hover:bg-[#0066DD] text-white'
            }`}
            style={{ borderRadius: 2 }}
            data-testid="analyze-btn"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              !analyzeAction.blocked && <Sparkle size={18} weight="fill" />
            )}
            {loading ? 'Analizando...' : analyzeAction.label}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 p-1 bg-ds-elevated border border-ds-border mb-6 w-fit"
        style={{ borderRadius: 2 }}
      >
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all ${
              activeTab === id ? 'bg-ds-surface text-ds-text' : 'text-ds-muted hover:text-ds-text'
            }`}
            style={{ borderRadius: 2 }}
          >
            <Icon
              size={15}
              weight={activeTab === id ? 'fill' : 'regular'}
              className={
                activeTab === id
                  ? id === 'youtube'
                    ? 'text-[#FF0000]'
                    : id === 'instagram'
                      ? 'text-[#E1306C]'
                      : ''
                  : ''
              }
            />
            {label}
          </button>
        ))}
      </div>

      {/* Tab: Análisis IA */}
      {activeTab === 'ai' && (
        <>
          {/* AI Analysis Results */}
          {analysis && (
            <div className="mb-8 space-y-4" data-testid="analysis-results">
              <div className="flex items-center gap-2 mb-1">
                <Robot size={18} className="text-[#007AFF]" weight="duotone" />
                <h2
                  className="text-ds-text font-semibold"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Insights de IA
                </h2>
              </div>

              <InsightCard
                icon={Lightning}
                title="Análisis Principal"
                content={analysis.insights}
                color="#007AFF"
                type="info"
              />

              {analysis.suggestions?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {analysis.suggestions.map((s, i) => (
                    <div key={i} className="card p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowUp size={14} className="text-[#32ADE6]" />
                        <span className="text-[#32ADE6] text-xs font-semibold uppercase tracking-widest">
                          Tip {i + 1}
                        </span>
                      </div>
                      <p className="text-ds-muted text-sm">{s}</p>
                    </div>
                  ))}
                </div>
              )}

              {analysis.patterns?.length > 0 && (
                <div>
                  <h3
                    className="text-ds-text text-sm font-semibold mb-3"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Patrones Detectados
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {analysis.patterns.map((p, i) => (
                      <div key={i} className="card p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span
                            className={`text-[10px] font-bold uppercase px-2 py-0.5 ${p.impact === 'alto' ? 'bg-green-500/15 text-green-400' : p.impact === 'medio' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-500/15 text-gray-400'}`}
                            style={{ borderRadius: 2 }}
                          >
                            {p.impact}
                          </span>
                        </div>
                        <p className="text-ds-text text-sm font-medium">{p.pattern}</p>
                        {p.example && <p className="text-ds-muted text-xs mt-1">Ej: {p.example}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysis.best_platform && (
                  <InsightCard
                    icon={CheckCircle}
                    title="Mejor Plataforma"
                    content={`${analysis.best_platform} es donde mejor funciona tu contenido`}
                    color="#32ADE6"
                    type="success"
                  />
                )}
                {analysis.best_tags?.length > 0 && (
                  <InsightCard
                    icon={Tag}
                    title="Tags Top"
                    content={analysis.best_tags.map(t => `#${t}`).join(' · ')}
                    color="#FFD60A"
                    type="warning"
                  />
                )}
              </div>
              {analysis.optimal_duration && (
                <InsightCard
                  icon={Clock}
                  title="Duración Óptima"
                  content={`Los videos de ~${analysis.optimal_duration} minutos tienen mejor performance en tu canal`}
                  color="#FF9F0A"
                  type="warning"
                />
              )}
            </div>
          )}

          {!analysis && !loading && projects.length === 0 && (
            <div className="card p-6 mb-8 text-center border-[rgba(0,122,255,0.2)]">
              <Robot size={36} className="text-[#007AFF] mx-auto mb-3" weight="duotone" />
              <p className="text-ds-text font-semibold mb-1">Sin datos para analizar</p>
              <p className="text-ds-muted text-sm">
                Crea proyectos en el Dashboard para obtener análisis de IA
              </p>
            </div>
          )}
        </>
      )}

      {/* Tab: Dashboard */}
      {activeTab === 'dashboard' && (
        <div className="space-y-8">
          <DashboardTab projects={projects} loadingProjects={loadingProjects} />
          {/* De dónde salen los números, y de dónde podrían salir */}
          <SourcesPanel projects={projects} onOpenTab={setActiveTab} />
        </div>
      )}

      {/* Tab: YouTube */}
      {activeTab === 'youtube' && <YouTubeTab />}

      {/* Tab: Instagram */}
      {activeTab === 'instagram' && <InstagramTab />}
    </div>
  )
}
